// useAdminGeolocation — geolocation watcher for admin / worker side.
// Day 13 PR-B3-2: workers hiding pins need to see their own position
// so they know "this pin is right where I'm standing" vs "I tapped
// the wrong spot". FieldMap renders an admin-style double-circle
// crosshair (static, ink-veil-40) — visually distinct from learner's
// breathing crosshair.
//
// Copied pattern from apps/user/src/hooks/useGeolocation.ts. Duplicated
// rather than shared because the two apps don't share hooks today.

import { useEffect, useState } from "react";

export interface AdminGeolocationState {
  coords: { lat: number; lng: number } | null;
  accuracy: number | null;
  status: "idle" | "watching" | "denied" | "unsupported" | "error";
  error: string | null;
}

const INITIAL: AdminGeolocationState = {
  coords: null,
  accuracy: null,
  status: "idle",
  error: null,
};

export function useAdminGeolocation(enabled: boolean = true): AdminGeolocationState {
  const [state, setState] = useState<AdminGeolocationState>(INITIAL);

  useEffect(() => {
    if (!enabled) return;
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setState({ ...INITIAL, status: "unsupported" });
      return;
    }
    setState((s) => ({ ...s, status: "watching" }));

    const id = navigator.geolocation.watchPosition(
      (pos) => {
        setState({
          coords: { lat: pos.coords.latitude, lng: pos.coords.longitude },
          accuracy: pos.coords.accuracy,
          status: "watching",
          error: null,
        });
      },
      (err) => {
        const denied = err.code === err.PERMISSION_DENIED;
        setState({
          coords: null,
          accuracy: null,
          status: denied ? "denied" : "error",
          error: err.message,
        });
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [enabled]);

  return state;
}
